<script>
import BaseView from "@/views/BaseView.vue";
import TruckDriverBlock from "@/views/trips/edit/blocks/TruckDriverBlock.vue";
import TrucksMixin from "@/views/trucks/TrucksMixin.vue";
import OmniTextarea from "@/components/inputs/Textarea.vue";
import OmniDialog from "@/components/OmniDialog.vue";
import { mapActions } from "pinia";
import { useGalleryStore } from "@/store/gallery.store";
import FacilityInfo from "@/views/trips/components/FacilityInfo.vue";
import { OrderFreightType } from "@/data/order";
import AlertMessage from "@/components/AlertMessage.vue";
import PalletSmallIcon from "@/assets/images/pallet-small.svg";
import WeightSmallIcon from "@/assets/images/weight-small.svg";
import { useOrder } from "@/composables/index.js";
import RouteStopIcon from "@/components/routeStop/RouteStopIcon.vue";
import DeletedChip from "@/components/chips/DeletedChip.vue";
import PackageVariantClose from "@/assets/images/package-variant-closed.svg";

const {
  getOrderRoute,
  STOP_DELIVERY,
  STOP_RELOAD,
  STOP_PICK_UP,
  getStopTime,
  timeTypesMap,
  getTravelOrderPaymentsAmount,
} = useOrder();

export default {
  name: "DispatchDialog",
  components: {
    DeletedChip,
    RouteStopIcon,
    AlertMessage,
    FacilityInfo,
    OmniDialog,
    OmniTextarea,
    TruckDriverBlock,
    PalletSmallIcon,
    WeightSmallIcon,
    PackageVariantClose,
  },
  mixins: [BaseView, TrucksMixin],
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    orders: {
      type: Array,
      required: true,
    },
    route: {
      type: Object,
      required: true,
    },
  },
  emits: ["update:modelValue", "update:route", "change"],
  data() {
    return {
      dispatchRoute: { travelOrders: [], routeStops: [], sharedFiles: [] },
      files: [],
    };
  },
  computed: {
    timeTypesMap() {
      return timeTypesMap;
    },
    STOP_DELIVERY() {
      return STOP_DELIVERY;
    },
    STOP_PICK_UP() {
      return STOP_PICK_UP;
    },
    STOP_RELOAD() {
      return STOP_RELOAD;
    },
    ordersNotes() {
      return this.orders
        .map((o) => o.generalNote || "")
        .filter((text) => text.length > 0)
        .join("\n");
    },
    showDialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    travelOrdersTotal() {
      let total = 0;

      for (const travelOrder of this.route.travelOrders) {
        total += getTravelOrderPaymentsAmount(travelOrder.payments);
      }
      return total;
    },
    tripStops() {
      return this.orders.flatMap((order) => order.tripStops);
    },
    routeStopsToShow() {
      return this.dispatchRoute.routeStops.filter((routeStop) => {
        if (!routeStop.id) {
          return !routeStop.isDeleted && routeStop.use;
        }

        return routeStop.use;
      });
    },
  },
  watch: {
    modelValue(value) {
      if (value) {
        this.prepareRoute();
      }
    },
  },
  mounted() {
    // For debug hot reload.
    if (this.modelValue) {
      this.prepareRoute();
    }
  },
  methods: {
    ...mapActions(useGalleryStore, ["setMedia", "setMediaId"]),
    getOrderRouteFunction(order) {
      return getOrderRoute(order);
    },
    getStopTimeFunction(stop) {
      return getStopTime(stop);
    },
    getStopFreights(stop) {
      if (this.route.freights.length === 0) {
        return [];
      }
      return stop.freights
        .map((freightId) => this.route.freights.find((f) => f.id === freightId && !f.isDeleted))
        .filter((f) => f);
    },
    getStopPiecesNumber(stop) {
      return this.getStopFreights(stop).reduce(
        (quantity, f) => (f && f.type === OrderFreightType.pieces ? quantity + parseInt(f.plannedQuantity) : quantity),
        0,
      );
    },
    getStopPalletsNumber(stop) {
      return this.getStopFreights(stop).reduce(
        (quantity, f) => (f && f.type === OrderFreightType.pallets ? quantity + parseInt(f.plannedQuantity) : quantity),
        0,
      );
    },
    getStopWeight(stop) {
      return this.getStopFreights(stop).reduce((totalWeight, f) => totalWeight + parseInt(f.plannedWeight), 0);
    },
    hasStackableFreight(stop) {
      return this.getStopFreights(stop).some((freight) => freight.stackable);
    },
    prepareRoute() {
      this.dispatchRoute = {
        ...this.route,
      };
      const sharedFiles = new Set(this.route.sharedFiles);
      const routeTag = "route:" + this.route.id + ":shared";

      const files = [];
      for (const travelOrder of this.route.travelOrders) {
        for (const file of this.getOrder(travelOrder).files) {
          // rate confirmation files can't be shared
          if (file.tags.includes("rateConfirmation")) continue;

          files.push({ ...file, sharedWithDriver: file.tags.includes(routeTag) || sharedFiles.has(file.id) });
        }
      }
      this.files = files;
      for (const routeStop of this.dispatchRoute.routeStops) {
        if (!routeStop.isReload) {
          routeStop.orderStop = this.getOrderStop(routeStop);
        }
      }
      this.dispatchRoute.routeStops.sort((a, b) => a.position - b.position);
    },
    preview(file) {
      this.setMedia([file]);
      this.setMediaId(file.id);
    },
    copyGeneralNotes() {
      this.dispatchRoute.driverNote = this.ordersNotes;
    },
    copyStopNote(stop) {
      stop.driverNote = stop.note;
    },
    getTOPaymentsAmount(payments) {
      return getTravelOrderPaymentsAmount(payments);
    },
    async save() {
      const route = { ...this.dispatchRoute, sharedFiles: [] };

      for (const file of this.files) {
        if (file.sharedWithDriver) {
          route.sharedFiles.push(file.id);
        }
      }

      for (const stop of route.routeStops) {
        stop.dispatchedTimeType = stop.timeType;
        stop.dispatchedTimeFrom = stop.timeFrom;
        stop.dispatchedTimeTo = stop.timeTo;
      }
      route.isDispatched = true;

      this.$emit("update:route", route);
      this.$emit("change", route);
      this.$emit("update:modelValue", false);
    },
    getOrderStop(routeStop) {
      return this.tripStops.find((s) => s.id === routeStop?.orderStopId || s.id === routeStop?.reloadStopId);
    },
    isStopUpdated(stop) {
      return !this.isStopDeleted(stop) && !!stop.oldFacilityId;
    },
    isStopDeleted(stop) {
      return stop.orderStop?.isDeleted;
    },
    getOrder(travelOrder) {
      return this.orders.find((o) => o.id === travelOrder.orderId);
    },
  },
};
</script>

<template>
  <omni-dialog
    v-model="showDialog"
    primary-action-label="Apply"
    title="Dispatch route"
    width="1280"
    @primary-action:click="save"
  >
    <div class="py-4">
      <div class="mb-8">
        <div
          v-for="(travelOrder, i) in dispatchRoute.travelOrders"
          :key="i"
          class="bg-grey-lighten-5 pa-3 rounded"
          style="border: 1px solid #e0e0e0"
        >
          <div class="d-flex align-center justify-space-between mb-4">
            <div class="d-flex">
              <div class="text-primary mr-4 font-weight-medium" data-qa="travel-order-number">
                TO #{{ travelOrder.id }}
              </div>
              <div class="text-grey-darken-2 font-weight-medium mr-4" data-qa="travel-order-rate">
                <span v-if="travelOrder.rate">{{ getTOPaymentsAmount(travelOrder.payments) }}$</span>
                <span v-else>{{ getTOPaymentsAmount(travelOrder.payments) }}$</span>
              </div>
              <div
                v-if="getOrder(travelOrder).protectFromFreezing"
                class="d-flex align-center ml-4"
                data-qa="protect-from-freezing"
              >
                <v-icon color="info" icon="mdi-snowflake" size="16"></v-icon>
                <div class="ml-1 font-size-12 font-weight-500 text-grey-darken-2">Protect from freezing</div>
              </div>
              <div v-if="getOrder(travelOrder).underTracking" class="d-flex align-center ml-4" data-qa="under-tracking">
                <v-icon color="success" icon="mdi-map-marker-outline" size="16"></v-icon>
                <div class="ml-1 font-size-12 font-weight-500 text-grey-darken-2">Under tracking</div>
              </div>
              <div v-if="getOrder(travelOrder).hazmat" class="d-flex align-center ml-4" data-qa="hazmat-commodity">
                <v-icon color="warning" icon="mdi-biohazard" size="16"></v-icon>
                <div class="ml-1 font-size-12 font-weight-500 text-grey-darken-2">Hazmat commodity</div>
              </div>
            </div>
            <div class="d-flex">
              <div v-if="getOrder(travelOrder).teamForBroker" class="d-flex align-center mr-8">
                <v-icon color="grey-darken-2" icon="mdi-account-multiple" size="16"></v-icon>
                <div class="ml-1 font-size-12 font-weight-500 text-grey-darken-2">Team for broker</div>
              </div>
              <router-link
                :to="{ name: 'customer-edit', params: { id: getOrder(travelOrder).customer.id } }"
                class="font-size-12 font-weight-500 text-primary mr-8 text-decoration-none"
                data-qa="dispatch-route-customer"
                target="_blank"
              >
                {{ getOrder(travelOrder).customer.name }}
              </router-link>
              <router-link
                :to="{ name: 'order-view', params: { id: getOrder(travelOrder)?.id } }"
                class="text-primary font-weight-medium font-size-12 text-decoration-none"
                data-qa="dispatch-route-order-number"
                target="_blank"
              >
                Order #{{ getOrder(travelOrder)?.number }}
              </router-link>
            </div>
          </div>
          <div
            class="font-weight-medium mb-3 text-grey-darken-3 text-subtitle-1"
            data-qa="dispatch-route-address-route"
          >
            {{ getOrderRouteFunction(getOrder(travelOrder)) }}
          </div>
          <div>
            <div class="d-flex align-center justify-space-between">
              <div class="font-size-16 text-grey-darken-1">
                <template v-if="getOrder(travelOrder).requiredDriverCertificates?.length">
                  Certificates:
                  <span class="font-weight-500 text-grey-darken-2" data-qa="dispatch-route-driver-certificates">
                    {{ getOrder(travelOrder).requiredDriverCertificates.join(", ") }}
                  </span>
                </template>
              </div>
              <div class="font-size-16 text-grey-darken-1">
                Check in as:
                <span class="font-weight-500 text-grey-darken-2" data-qa="dispatch-route-check-in-as">
                  {{ getOrder(travelOrder).checkInAs }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="text-right mt-3">
          <div class="text-grey-darken-3">
            Total driver rate:
            <span class="font-weight-medium" data-qa="dispatch-route-total-driver-rate">{{ travelOrdersTotal }}$</span>
          </div>
        </div>
      </div>

      <div class="text-grey-darken-3 mb-3 font-weight-medium text-subtitle-1">General note</div>
      <div class="bg-grey-lighten-4 pa-3 rounded">
        <omni-textarea v-model="ordersNotes" data-qa="old-note" label="General note" readonly="readonly" rows="2">
        </omni-textarea>
        <div class="my-2 text-center">
          <v-btn color="primary" icon="mdi-arrow-down" size="20" variant="text" @click="copyGeneralNotes"></v-btn>
        </div>
        <omni-textarea
          v-model="dispatchRoute.driverNote"
          class="bg-white"
          data-qa="new-note"
          label="General note for driver"
          rows="2"
        ></omni-textarea>
      </div>

      <div>
        <div class="text-grey-darken-3 mb-3 font-weight-medium text-subtitle-1 mt-4">Dispatch info</div>

        <div class="mb-8">
          <div
            v-for="(stop, i) in routeStopsToShow"
            :key="i"
            :class="{ 'stop-deleted': isStopDeleted(stop) }"
            class="bg-grey-lighten-4 pa-3 mb-5 rounded"
          >
            <v-row class="dispatch-info__blocks">
              <v-col class="dispatch-info__block">
                <deleted-chip v-if="stop.isDeleted" class="mb-2" />
                <div class="d-flex flex-column height-100 justify-space-between">
                  <div>
                    <div class="d-flex justify-space-between">
                      <div class="d-flex align-center flex-wrap mb-6">
                        <span class="d-flex align-center mr-8">
                          <route-stop-icon
                            :is-deleted="stop.isDeleted"
                            :is-reload="stop.isReload"
                            :type="stop.type"
                            class="mr-2"
                          ></route-stop-icon>
                          <span
                            :class="isStopDeleted(stop) ? 'text-grey' : 'text-blue-grey-darken-1'"
                            class="font-weight-600 font-size-12"
                            data-qa="dispatch-info-stop-type"
                          >
                            {{ stop.orderStop?.name || stop.name }}
                          </span>
                        </span>
                        <span
                          v-if="getStopPiecesNumber(stop)"
                          :class="isStopDeleted(stop) ? 'text-grey' : 'text-grey-darken-2'"
                          class="d-flex align-center font-size-12 font-weight-medium mr-4"
                          data-qa="dispatch-info-freight-pieces"
                        >
                          <pallet-small-icon class="mr-2" />
                          {{ getStopPiecesNumber(stop) }} pieces
                        </span>
                        <span
                          v-if="getStopPalletsNumber(stop)"
                          :class="isStopDeleted(stop) ? 'text-grey' : 'text-grey-darken-2'"
                          class="d-flex align-center font-size-12 font-weight-medium mr-4"
                          data-qa="dispatch-info-freight-pallets"
                        >
                          <pallet-small-icon class="mr-2" />
                          {{ getStopPalletsNumber(stop) }} pallets
                        </span>
                        <span
                          :class="isStopDeleted(stop) ? 'text-grey' : 'text-grey-darken-2'"
                          class="d-flex align-center font-size-12 font-weight-medium"
                        >
                          <weight-small-icon class="mr-2" />
                          {{ getStopWeight(stop) }} lbs
                        </span>
                        <package-variant-close v-if="hasStackableFreight(stop)" class="ml-3" />

                        <router-link
                          v-if="!isStopDeleted(stop) && stop.order"
                          :to="{ name: 'order-view', params: { id: stop.order?.id } }"
                          class="text-primary font-size-12 font-weight-medium text-decoration-none ml-8"
                          data-qa="dispatch-info-order-number"
                          target="_blank"
                        >
                          Order #{{ stop.order?.number }}
                        </router-link>
                        <span v-if="isStopDeleted(stop)" class="text-grey font-size-12 font-weight-medium">
                          Order #{{ stop.order?.number }}
                        </span>
                      </div>
                      <div class="d-flex align-center">
                        <div
                          :class="isStopDeleted(stop) ? 'text-grey' : 'text-grey-darken-1'"
                          class="d-flex align-center font-weight-medium mb-6"
                          data-qa="dispatch-info-stop-time"
                        >
                          <v-icon class="mr-2" size="20">mdi-clock-outline</v-icon>
                          Route stop time - {{ timeTypesMap[stop.timeType] }}: {{ getStopTimeFunction(stop) }}
                        </div>
                        <div
                          v-if="isStopUpdated(stop)"
                          class="badge bg-warning pa-2 rounded-te rounded-bs font-weight-600 ml-4"
                        >
                          UPDATED
                        </div>
                        <div
                          v-if="isStopDeleted(stop)"
                          class="badge bg-grey-darken-1 pa-2 rounded-te rounded-bs font-weight-600 ml-4"
                        >
                          DELETED
                        </div>
                      </div>
                    </div>
                    <div v-if="stop.facility">
                      <facility-info
                        :disabled="isStopDeleted(stop)"
                        :facility="stop.facility"
                        :updated="!!stop.oldFacilityId"
                      ></facility-info>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
            <v-row class="align-center">
              <v-col class="pr-2">
                <omni-textarea
                  v-model="stop.note"
                  :disabled="isStopDeleted(stop)"
                  data-qa="order-stop-note"
                  label="Order stop note"
                  readonly
                  rows="2"
                />
              </v-col>
              <v-col class="pa-0 arrow-container">
                <v-btn
                  :disabled="isStopDeleted(stop)"
                  color="primary"
                  icon="mdi-arrow-right"
                  size="20"
                  variant="text"
                  @click="copyStopNote(stop)"
                ></v-btn>
              </v-col>
              <v-col class="pl-2">
                <omni-textarea
                  v-model="stop.driverNote"
                  :disabled="isStopDeleted(stop)"
                  class="bg-white"
                  data-qa="driver-instruction"
                  label="Driver instructions"
                  rows="2"
                />
              </v-col>
            </v-row>
            <alert-message
              v-if="isStopDeleted(stop)"
              bg-color="indigo-lighten-5"
              border-color="none"
              class="mt-3"
              color="indigo"
              icon="mdi-information-outline"
              icon-color="indigo"
            >
              Stop will be removed from trip after re-dispatch confirmation
            </alert-message>
          </div>
        </div>

        <div class="mb-8">
          <div class="mb-6">
            <div class="text-grey-darken-3 font-weight-medium text-subtitle-1">Files</div>
            <div class="text-grey-darken-2 mt-3">Select or upload the files that will be sent to the driver</div>
          </div>
          <div>
            <v-row>
              <v-col v-for="file in files" :key="file.id" lg="3" md="4" sm="6" xl="3" xs="12" xxl="3">
                <div class="rounded d-flex flex-column" style="border: 1px solid #e0e0e0; height: 364px">
                  <div class="overflow-hidden">
                    <img :src="getPreviewPath(file)" alt="" class="w-100 rounded-t" />
                  </div>
                  <div class="pa-4" style="border-top: 1px solid #e0e0e0">
                    <div class="mb-4 overflow-hidden">
                      <v-checkbox v-model="file.sharedWithDriver" color="primary" density="compact" hide-details>
                        <template #label>
                          <div class="ml-2 d-flex align-center">
                            <v-icon class="mr-2" color="primary" size="20">mdi-file-document-outline</v-icon>
                            <span class="text-grey-darken-4">{{ file.name }}</span>
                          </div>
                        </template>
                      </v-checkbox>
                    </div>
                    <v-btn
                      block="block"
                      class="text-uppercase"
                      color="primary"
                      variant="outlined"
                      @click="preview(file)"
                    >
                      View
                    </v-btn>
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>
        </div>

        <div v-if="dispatchRoute.truck" class="mb-8">
          <div class="d-flex align-center justify-space-between mb-3">
            <div class="text-grey-darken-3 font-weight-medium text-subtitle-1">Dispatching contacts</div>
            <div class="d-flex flex-wrap">
              <router-link
                :to="{ name: 'truck-view', params: { id: dispatchRoute.truck.id } }"
                class="text-primary text-decoration-none mr-3"
                data-qa="dispatching-contact-truck-number"
                target="_blank"
              >
                Truck {{ dispatchRoute.truck.number }}
              </router-link>
              <span class="text-grey-darken-2" data-qa="dispatching-contacts-freights-summary">
                {{ getTruckType(dispatchRoute.truck.type) }}, {{ dispatchRoute.truck.trailer.length }}″ x
                {{ dispatchRoute.truck.trailer.height }}″ x {{ dispatchRoute.truck.trailer.width }}″,
                {{ dispatchRoute.truck.trailer.payload }} lbs
              </span>
            </div>
          </div>
          <div>
            <v-row>
              <v-col v-if="dispatchRoute.truck?.driver" lg="3" md="4" sm="6" xl="3" xs="12" xxl="3">
                <truck-driver-block :driver="dispatchRoute.truck.driver" />
              </v-col>

              <v-col v-if="dispatchRoute.truck?.driver.contactPerson" lg="3" md="4" sm="6" xl="3" xs="12" xxl="3">
                <truck-driver-block :driver="dispatchRoute.truck.driver.contactPerson" is-financially-responsible />
              </v-col>
            </v-row>
          </div>
        </div>
      </div>
    </div>
  </omni-dialog>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.stop-deleted {
  border: 1px solid $grey-lighter;
}

.arrow-container {
  flex: 0 0 20px;
}

.badge {
  height: 36px;
  margin-top: -32px;
  margin-right: -12px;
}
</style>
